import React, { useEffect, useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import { connect, useDispatch } from 'react-redux';
import isFunction from 'lodash/isFunction';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import TileHeader from './TileHeader/TileHeader';
import ProgressBar from './ProgressBar/ProgressBar';
import Coins from './Coins/Coins';
import Tags from './Tags/Tags';
import TileFooter from './TileFooter/TileFooter';
import Skeleton, { skeletonBg } from '../Skeleton/Skeleton';
import config from '../../../config/config.json';
import { usePromise } from '../../../hooks/usePromise/usePromise';
import { getChampion } from '../../../services/createHPoolService';
import NFTImage from '../../Pages/HPool/NFTImage/NFTImage';
import { Text, CustomLink } from '..';
import {
  canViewPool as _canViewPool,
  defaultRenderedStats,
} from '../../Pages/Champion/ChampionChecker/ChampionOwner/statusMapper';
import { usePoolInfo } from '../../../hooks/usePoolInfo';
import './PoolTile.scss';
import WalletLauncher from '../../hoc/WalletLauncher/WalletLauncher';
import ShowAt from '../ShowAt/ShowAt';
import { displayFloats } from '../../../utils/numbersDisplay';
import TimeLeft from './TileHeader/TimeLeft/TimeLeft';
import TileStatus from './TileHeader/TileStatus/TileStatus';
import { getBusdBalance } from '../../../services/assetsService';

// TODO: Split to VpoolTile and HpoolTile
const PoolTile = ({
  statusObject = {},
  refreshPools,
  tile,
  loading: parentLoading,
  updatePools,
  isMine,
  account,
  whitelistedPool,
  championId,
}) => {
  const {
    hpool_nft_web3_token_id: nftWebToken,
    web3_hpool_id: webHpoolId,
    launched_at: launchDate,
    id: hpoolId,
    assets,
    name: title,
    tags_str: tagsString,
    champion_id: poolOwnerId,
    contract_address: contractAddress,
  } = tile || {};
  const [getChampionData, champData, chmapDataLoading] = usePromise(getChampion);
  const [getBusdBalanceReq, busdBalance] = usePromise(getBusdBalance);

  const {
    champion: { profile_media_url: profileImg, name, handle },
  } = champData || { champion: {} };

  const dispatch = useDispatch();
  const { push } = useHistory();
  const { color = skeletonBg, percentage } = statusObject;
  const isVpool = tile.is_vpool;
  const poolTypePath = clsx({ vpool: isVpool, hpool: !isVpool });

  const {
    numberOfSubscribers,
    ticketsReservedFrom,
    ticketsReservedTo,
    formattedCapacity,
    capacityInUsd,
    formattedTimeLeft,
    loading: poolInfoLoading,
    isSubscribed,
    infoIsFetched,
    ownedTickets,
    totalHordStaked,
    champInitDepo,
    userTotalPoolTokens,
    timeLeftPercentage,
    championAddress,
    vpoolInfo,
  } = usePoolInfo({
    webHpoolId,
    nftWebToken,
    status: tile.status,
    contractAddress,
    isVpool: tile.is_vpool,
    poolChain: tile.chain_id || config.network,
  });
  const forammtedLaunchDate = useMemo(() => new Date(launchDate).toDateString(), [launchDate]);

  const allLoadings = [parentLoading, !tile.champion_id].some((loading) => loading);
  const formattedCoins = useMemo(
    () => assets?.map((asset) => ({ name: asset.token_name, image: asset.token_image_thumb })),
    [assets],
  );
  const { chain_id: chainId } = tile;

  const showTimeLeft = percentage !== 100;

  const tileInfo = {
    hpool: tile,
    push,
    whitelistedPool,
    dispatch,
    refreshPools,
    totalTikcetsMinted: ownedTickets,
    isSubscribed,
    userTotalPoolTokens,
    championAddress,
    account,
    timeLeftPercentage,
    busdBalance,
    ...vpoolInfo,
    championId,
    poolOwnerId,
  };
  useEffect(() => {
    // pass the total cap to update the parent pool array
    const poolWithInfo = { ...tile, capacityInUsd };
    updatePools(poolWithInfo);
  }, [capacityInUsd]);

  useEffect(() => {
    if (!account) return;
    getBusdBalanceReq(account);
  }, [account]);

  useEffect(() => {
    if (!champData?.champion?.id) return;
    const { champion } = champData;
    const poolWithChamp = {
      ...tile,
      champion: {
        profileImg: champion.profile_media_url,
        name: champion.name,
        twitterHandle: champion.handle,
      },
    };
    updatePools(poolWithChamp);
  }, [champData?.champion?.id]);

  useEffect(() => {
    if (!poolOwnerId || champData?.champion?.id) return;
    getChampionData(poolOwnerId);
  }, [poolOwnerId]);

  const statsInfo = {
    launchDate: forammtedLaunchDate,
    reserved: { from: ticketsReservedFrom, to: ticketsReservedTo },
    capacityInUsd,
    numberOfSubscribers,
    ownedTickets,
    totalHordStaked,
    champInitDepo,
    tokenPrice: displayFloats(tile.hpool_token_price, 3),
    formattedCapacity,
    ...vpoolInfo,
    coinsAndTags: (
      <div className="d-flex gap-md">
        <Coins coins={formattedCoins} />
        <Tags tags={tagsString} />
      </div>
    ),
  };

  const isMyPool = useMemo(() => isMine(tile), [tile]);
  const { renderedStats: _renderedStats, showDefaultStats } = statusObject || {};
  const renderedStats = (isMyPool && showDefaultStats) || !_renderedStats ? defaultRenderedStats : _renderedStats;
  const {
    showTimeEnded,
    showNextStage,
    onClick,
    alwaysClickable,
    btnTitle,
    title: poolTileTitle,
    stepIndex,
    ignoreTimeLeft,
    willLinkTo,
  } = statusObject || {};

  const btnHidden = (!onClick || !formattedTimeLeft || isMyPool) && !alwaysClickable;
  const canViewPool = useMemo(() => _canViewPool({ isMine: isMyPool, status: tile.status }), [tile.status, isMine]);

  const btnText = isFunction(btnTitle) ? btnTitle(tileInfo) : btnTitle;
  const renderedPercentage = percentage || timeLeftPercentage;

  return (
    <div className="pool-tile">
      <CustomLink
        disabled={!canViewPool || allLoadings}
        to={isFunction(willLinkTo) ? willLinkTo(isVpool) : willLinkTo || `/${poolTypePath}/${hpoolId}`}
        className="pure-link"
      >
        <TileHeader
          statusObject={statusObject}
          loading={!profileImg || chmapDataLoading}
          color={color}
          name={name}
          twitterHandle={handle}
          profileImg={profileImg}
          chainId={chainId}
        />
        <div className="pool-legend-progressbar">
          <ProgressBar value={renderedPercentage} color={allLoadings ? skeletonBg : color} />
        </div>
        <div className="pool-tile-main">
          <ShowAt at={!allLoadings}>
            <div className="main-top" style={{ color }}>
              <TileStatus
                poolTileTitle={isFunction(poolTileTitle) ? poolTileTitle(isVpool) : poolTileTitle}
                stepInfo={statusObject}
              />
              <TimeLeft
                showNextStage={showNextStage}
                formattedTimeLeft={formattedTimeLeft}
                showTimeEnded={showTimeEnded}
                infoIsFetched={infoIsFetched}
                showTimeLeft={showTimeLeft}
                ignoreTimeLeft={ignoreTimeLeft}
              />
            </div>
          </ShowAt>
          <div className="main-body">
            <Skeleton loading={allLoadings} className="tile-image">
              <NFTImage loading={allLoadings} img={tile?.hpool_token_icon} nftId={tile.hpool_nft_id} />
            </Skeleton>
            <Skeleton loading={allLoadings} className="tile-right">
              <Text dontTranslate className="title" text={title} />
              <div className="details">
                {renderedStats?.map((stat) => (
                  <div key={stat?.prop} className="stat-prop">
                    <Text
                      className="key"
                      dontTranslate={!!stat.renderLabel}
                      text={stat?.renderLabel?.(`pool_tile.${stat?.title}`) || `pool_tile.${stat?.title}`}
                      style={{ ...stat?.style }}
                    />
                    <Skeleton loading={poolInfoLoading || vpoolInfo?.vPoolInfoLoading} minHeight="2vh">
                      <Text
                        dontTranslate
                        className="value"
                        text={stat?.render?.(statsInfo?.[stat?.prop]) || statsInfo?.[stat?.prop]}
                      />
                    </Skeleton>
                  </div>
                ))}
              </div>
            </Skeleton>
          </div>
        </div>
      </CustomLink>
      <ShowAt at={!btnHidden}>
        <WalletLauncher action={btnText} className="pool-tile-footer">
          <TileFooter tileInfo={tileInfo} statusObject={statusObject} text={btnText} func={onClick} color={color} />
        </WalletLauncher>
      </ShowAt>
    </div>
  );
};

PoolTile.defaultProps = {
  statusObject: {},
  championId: null,
};

PoolTile.propTypes = {
  statusObject: PropTypes.object,
  loading: PropTypes.bool.isRequired,
  whitelistedPool: PropTypes.array.isRequired,
  tile: PropTypes.object.isRequired,
  refreshPools: PropTypes.func.isRequired,
  updatePools: PropTypes.func.isRequired,
  isMine: PropTypes.func.isRequired,
  account: PropTypes.string.isRequired,
  championId: PropTypes.number,
};

const mapStateToProps = ({ champion, wallet, createHPool }) => ({
  isMine: (pool) => pool.champion_id === champion?.championData?.champion_id,
  account: wallet.account,
  whitelistedPool: createHPool.vpool.whitelistedUserPools,
  championId: champion?.championData?.champion_id,
});

export default connect(mapStateToProps)(PoolTile);
